import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Hva kan jeg gjøre hvis jeg får inkassokrav?",
  "Hvordan lager jeg et realistisk budsjett?",
  "Hva er en gjeldsordning, og hvem kan få det?",
];

const getSessionId = () => {
  let id = localStorage.getItem("trygg-chat-session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("trygg-chat-session", id);
  }
  return id;
};

const renderText = (text) => {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  return text.split("\n").map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-2" />;
    if (t.startsWith("#")) return <p key={i} className="font-semibold mt-1" dangerouslySetInnerHTML={{ __html: inline(t.replace(/^#+\s*/, "")) }} />;
    if (t.startsWith("- ")) return <p key={i} className="pl-3" dangerouslySetInnerHTML={{ __html: "• " + inline(t.slice(2)) }} />;
    return <p key={i} dangerouslySetInnerHTML={{ __html: inline(t) }} />;
  });
};

export default function AiAdvisor() {
  const STORAGE_KEY = "gv-chat-history";
  const [messages, setMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)));
    } catch {}
  }, [messages]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: content, history: messages.slice(-10) }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Beklager — noe gikk galt. Prøv å sende spørsmålet på nytt." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <section id="raadgiver" data-testid="ai-advisor-section" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          className="lg:col-span-4"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-terracotta">AI-rådgiver</p>
          <h2 className="mt-3 font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-primary">
            Spør om økonomi — få svar med en gang
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Still spørsmål om gjeld, inkasso, budsjett eller rettigheter. Rådgiveren svarer på norsk
            og peker deg til riktige offentlige sider.
          </p>
          <div className="mt-8 flex items-start gap-3 text-sm text-muted-foreground">
            <ShieldCheck size={15} className="text-primary shrink-0 mt-0.5" />
            <span>Veiledningen er generell og erstatter ikke profesjonell rådgivning. Ikke del personnummer eller sensitive opplysninger.</span>
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-8 bg-surface border border-border rounded-3xl overflow-hidden flex flex-col"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div ref={listRef} data-testid="ai-chat-messages" className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-5 max-h-[480px] min-h-[320px]">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-10">
                <span className="w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                  <Sparkles size={20} />
                </span>
                <p className="text-muted-foreground text-sm max-w-sm">
                  Hei! Jeg kan hjelpe deg med generelle spørsmål om økonomi og gjeld. Hva lurer du på?
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="ai-suggestion-chip" onClick={() => send(s)}
                      className="text-xs font-semibold bg-accent text-accent-foreground px-4 py-2.5 rounded-full hover:bg-primary hover:text-primary-foreground transition-colors duration-200">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={m.role === "user" ? "ai-chat-message-user" : "ai-chat-message-assistant"}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-background border border-border rounded-bl-md"
                }`}>
                  {m.content ? renderText(m.content) : (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-border p-4 lg:p-5 flex gap-3 bg-background/60"
          >
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Skriv spørsmålet ditt her …"
              className="flex-1 bg-surface border border-border rounded-full px-5 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
            />
            <button
              data-testid="ai-chat-send-btn"
              type="submit"
              disabled={streaming || !input.trim()}
              className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none"
              aria-label="Send melding"
            >
              <Send size={17} />
            </button>
          </form>
          <p data-testid="ai-disclaimer" className="px-5 pb-4 text-[11px] leading-relaxed text-muted-foreground text-center">
            Svar fra AI kan inneholde feil eller være utdatert — sjekk alltid mot offentlige kilder som nav.no før du tar beslutninger.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
